import * as React from 'react';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {capitalizeString} from 'utilities';
import {PublicCategory, GraphQLData, Shop} from 'interfaces';
import {
  CategoryCardLayout,
  Hero,
  HowExchange,
  InfoBar,
  ListingLayout,
  SocialProof,
  SubscriptionLayout,
  Support,
  WhyExchange,
} from 'components/Marketplace';
import {Breadcrumb, ContentBlock} from 'components/UIKit';
import styles from '../Category.scss';
import * as locales from './locales';

export interface CategoryComponentProps {
  category: GraphQLData<PublicCategory>;
  breadcrumbs: Breadcrumb[];
  categoriesShopPath: string;
  authenticityToken: string;
  shopsPath: string;
  bestListings: Shop[];
  soldListings: Shop[];
  soldListingsPath: string;
}

function CategoryComponent(props: CategoryComponentProps & WithI18nProps) {
  const {
    category,
    breadcrumbs,
    categoriesShopPath,
    authenticityToken,
    shopsPath,
    bestListings,
    soldListings,
    soldListingsPath,
    i18n,
  } = props;

  const {
    heading,
    contextualTitle,
    subhead,
    imageUrl,
    pluralTypeLabel,
    typeLabel,
    secondaryImageUrl,
    seoData,
    secondarySeoData,
    title,
    similarCategories,
  } = category.data.category;
  const showSeoContent = Boolean(seoData.content && seoData.heading);
  const showSecondSeoContent = Boolean(secondarySeoData.content && secondarySeoData.heading);
  const showSimilarCategories = similarCategories.length > 0;
  const showBestListings = bestListings.length > 0;
  const showSoldListings = soldListings.length > 0;

  return (
    <>
      <InfoBar breadcrumbs={breadcrumbs} />
      <Hero
        heading={
          heading || i18n.translate('heading', {category: capitalizeString(contextualTitle)})
        }
        subhead={subhead || i18n.translate('subhead')}
        cta={i18n.translate('cta')}
        url={categoriesShopPath}
        imageUrl={imageUrl}
      />
      {showSeoContent && (
        <ContentBlock heading={seoData.heading} subhead={seoData.subhead} copy={seoData.content} />
      )}
      {showBestListings && (
        <ListingLayout
          heading={i18n.translate('bestListings.heading', {category: contextualTitle})}
          subhead={i18n.translate('bestListings.subhead', {category: contextualTitle})}
          shops={bestListings}
          cta={i18n.translate('bestListings.cta')}
          ctaPath={categoriesShopPath}
          className={styles.BackgroundSilverLight}
        />
      )}
      <WhyExchange />
      {showSecondSeoContent && (
        <ContentBlock
          heading={secondarySeoData.heading}
          subhead={secondarySeoData.subhead}
          copy={secondarySeoData.content}
          theme="purpleDark"
        />
      )}
      {showSimilarCategories && (
        <CategoryCardLayout
          heading={i18n.translate('similarCategories.heading', {
            types: pluralTypeLabel,
          })}
          subhead={i18n.translate('similarCategories.subhead', {
            type: typeLabel,
          })}
          categories={similarCategories}
          className={styles.BackgroundSilverLight}
        />
      )}
      <SocialProof ctaPath={shopsPath} />
      <SubscriptionLayout
        authenticityToken={authenticityToken}
        imageUrl={secondaryImageUrl}
        className={styles.SubscriptionContainer}
      />
      {showSoldListings && (
        <ListingLayout
          heading={i18n.translate('soldListings.heading', {category: title})}
          subhead={i18n.translate('soldListings.subhead', {category: title})}
          shops={soldListings}
          cta={i18n.translate('soldListings.cta', {category: title})}
          ctaPath={soldListingsPath}
          className={styles.BackgroundSilverLight}
        />
      )}
      <HowExchange />
      <Support />
    </>
  );
}

export const Category = withI18n({
  id: 'Category',
  fallback: locales.en,
})(CategoryComponent);
